import {
  Box, Button, HStack, Icon, Input, Popover, PopoverArrow,
  PopoverBody, PopoverContent, PopoverFooter, PopoverTrigger, Text,
  useDisclosure, VStack,
} from "@chakra-ui/react";
import { ReactNode, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import moment from "moment-timezone";
import CDS from "design-system";
import type { FieldTagTone } from "design-system/components/FieldTag";

interface Props {
  /** ISO string of the currently scheduled time. When undefined, the picker pre-fills with now. */
  currentIso?: string;
  /**
   * The timezone the picker reads and writes in. Treated as the canonical
   * timezone for both display formatting (when the consumer rebuilds `displayText`)
   * and ISO serialization. Should match the message/email's `timezone` field.
   */
  timezone: string;
  /**
   * Optional sentence shown below the inputs, e.g.
   * "This will fire from America/Los_Angeles, the new hire's timezone."
   * Falls back to the raw `timezone` string when omitted.
   */
  timezoneDescription?: ReactNode;
  /** Pre-formatted text shown in the pill body. */
  displayText: string;
  /** Fired on Save with the new ISO string. */
  onSave: (iso: string) => void;
  /** When false, renders a plain read-only pill — no popover. */
  isEditable: boolean;
  /** Save-in-flight indicator. */
  isSaving?: boolean;
  /** Pill label, defaults to "Scheduled". */
  label?: string;
  /** Visual tone — pass "critical" to flag a problem (e.g. send time is in the past). */
  tone?: FieldTagTone;
}

function toDateInputValue(iso: string | undefined, tz: string): string {
  return (iso ? moment.tz(iso, tz) : moment.tz(tz)).format("YYYY-MM-DD");
}

function toTimeInputValue(iso: string | undefined, tz: string): string {
  return (iso ? moment.tz(iso, tz) : moment.tz(tz)).format("HH:mm");
}

/**
 * Builds the popover timezone hint sentence:
 *   - "This will fire from {tz}, the new hire's timezone." when they match
 *   - "This will fire from {tz}; the new hire's timezone is {other}." when they differ
 *   - "This will fire from {tz} (your browser timezone)." when both resource + new hire TZs are absent
 */
export function buildTimezoneDescription({
  timezone, resourceTz, newHireTz,
}: { timezone: string; resourceTz?: string; newHireTz?: string }): ReactNode {
  const Tz = ({ children }: { children: string }) => <Text as="span" fontWeight="medium">{children}</Text>;

  if (!resourceTz && !newHireTz) {
    return <>This will fire from <Tz>{timezone}</Tz> (your browser timezone).</>;
  }
  if (newHireTz && timezone !== newHireTz) {
    return <>This will fire from <Tz>{timezone}</Tz>; the new hire&rsquo;s timezone is <Tz>{newHireTz}</Tz>.</>;
  }
  return <>This will fire from <Tz>{timezone}</Tz>, the new hire&rsquo;s timezone.</>;
}

export default function EditableScheduledFieldTag({
  currentIso, timezone, timezoneDescription, displayText, onSave, isEditable,
  isSaving = false, label = "Scheduled", tone = "default",
}: Props) {
  const { isOpen, onOpen: openPopover, onClose } = useDisclosure();
  const [dateValue, setDateValue] = useState(() => toDateInputValue(currentIso, timezone));
  const [timeValue, setTimeValue] = useState(() => toTimeInputValue(currentIso, timezone));

  // Re-seed inputs from the latest persisted value each time the popover opens,
  // so saves elsewhere or cancelled edits don't leave stale draft values.
  const onOpen = () => {
    setDateValue(toDateInputValue(currentIso, timezone));
    setTimeValue(toTimeInputValue(currentIso, timezone));
    openPopover();
  };

  if (!isEditable) {
    return <CDS.FieldTag label={label} tone={tone}>{displayText}</CDS.FieldTag>;
  }

  const handleSave = () => {
    if (!dateValue || !timeValue) return;
    const combined = moment.tz(`${dateValue}T${timeValue}`, timezone);
    if (!combined.isValid()) return;
    onSave(combined.toISOString());
    onClose();
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" closeOnBlur>
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          cursor="pointer"
          _hover={tone === "critical"
            ? { "& > div": { borderColor: "red.300", bg: "red.100" } }
            : { "& > div": { borderColor: "gray.300", bg: "gray.50" } }}
        >
          <CDS.FieldTag label={label} tone={tone}>
            <Box as="span" display="inline-flex" alignItems="center" gap="6px">
              <Box as="span">{displayText}</Box>
              <Icon as={FiCalendar} boxSize={3} color={tone === "critical" ? "red.500" : "fg.muted"} />
            </Box>
          </CDS.FieldTag>
        </Box>
      </PopoverTrigger>
      <PopoverContent w="260px">
        <PopoverArrow />
        <PopoverBody>
          <VStack spacing={3} align="stretch">
            <Box>
              <Text fontSize="xs" color="fg.muted" mb={1} fontWeight="medium">Date</Text>
              <Input
                type="date"
                size="sm"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="xs" color="fg.muted" mb={1} fontWeight="medium">Time</Text>
              <Input
                type="time"
                size="sm"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
              />
            </Box>
            <Text fontSize="xs" color="fg.subtle" lineHeight="1.4">
              {timezoneDescription ?? timezone}
            </Text>
          </VStack>
        </PopoverBody>
        <PopoverFooter borderTop="1px solid" borderColor="gray.100" pt={2}>
          <HStack justify="flex-end" spacing={2}>
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              colorScheme="cdsIndigo"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={!dateValue || !timeValue}
            >
              Save
            </Button>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
}
